import { useEffect, useState } from "react";
import {
  ProcurementPackageIcon,
  InspectionTaskIcon,
  DocumentIcon,
  WorkSectionIcon,
  TaskListIcon,
  SuccessIcon,
  AddIcon,
  DeleteIcon,
} from "@/lib/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { DeadlineOffsetEditor } from "./DeadlineOffsetEditor";
import { offsetFromColumns } from "./deadlineOffset";
import { missingNames, type TemplateTaskKind } from "./templateTaskCatalog";
import type { DraftTaskPatch } from "./useTemplateSetDraft";
import {
  ColumnLayout,
  Row,
  SectionHeader,
  DetailPanel,
  DetailField,
  EmptyCol,
  useColumnState,
  type ColumnDescriptor,
  type ColumnNavigation,
} from "@/components/columnView/ColumnLayout";
import type {
  PreviewPackage,
  PreviewTask,
  PreviewSubtask,
  PreviewInspectionTask,
  PreviewDocumentTask,
  PreviewWorkSection,
} from "./TaskTemplatePreviewTable";

// ─── Editing API ──────────────────────────────────────────────────────────────

/** Callbacks the dialog hands down so each detail panel can edit its own row. */
export interface TemplateEditingApi {
  onPatchTask: (id: string, patch: DraftTaskPatch, isNew: boolean) => void;
  onAddTask: (args: {
    kind: TemplateTaskKind;
    name: string;
    packageId: string | null;
    workSectionId: string | null;
  }) => void;
  onAddSubtask: (args: {
    parentId: string;
    isNewParent: boolean;
    packageId: string | null;
    workSectionId: string | null;
  }) => void;
  onDeleteTask: (args: { id: string; isNew: boolean }) => void;
  onUndeleteTask: (id: string) => void;
}

function AddTaskRow({
  kind,
  present,
  label,
  onAdd,
}: {
  kind: TemplateTaskKind;
  present: string[];
  label: string;
  onAdd: (name: string) => void;
}) {
  const options = missingNames(kind, present);
  if (options.length === 0) {
    return (
      <div className="px-3 py-2 text-[11px] text-muted-foreground">
        Kaikki tehtävät lisätty
      </div>
    );
  }
  return (
    <div className="px-3 py-2">
      <Select value="" onValueChange={onAdd}>
        <SelectTrigger className="h-7 px-2 text-[11px]">
          {/* A div, not a span: SelectTrigger applies line-clamp-1 to a direct
              span child, whose display:-webkit-box would stack the icon above
              the label. */}
          <div className="flex items-center gap-1 text-muted-foreground truncate">
            <AddIcon className="h-3 w-3 shrink-0" />
            <span className="truncate">{label}</span>
          </div>
        </SelectTrigger>
        <SelectContent>
          {options.map((name) => (
            <SelectItem key={name} value={name} className="text-[11px]">
              {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function DeletedNotice({ onUndo }: { onUndo: () => void }) {
  return (
    <div className="rounded-md border border-muted-foreground/25 bg-muted/40 px-2.5 py-2 space-y-2">
      <p className="text-xs text-muted-foreground leading-tight">
        Poistetaan tallennettaessa.
      </p>
      <Button
        variant="outline"
        size="sm"
        className="h-7 w-full text-xs"
        onClick={onUndo}
      >
        Kumoa poisto
      </Button>
    </div>
  );
}

function DeleteTaskButton({
  onDelete,
  hasChildren,
}: {
  onDelete: () => void;
  hasChildren?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs text-destructive hover:text-destructive"
        onClick={() => (hasChildren ? setConfirming(true) : onDelete())}
      >
        <DeleteIcon className="mr-1.5 h-3 w-3" />
        Poista tehtävä
      </Button>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Poista tehtävä"
        description="Tehtävä ja sen alitehtävät merkitään poistettaviksi. Poisto tapahtuu vasta tallennettaessa."
        confirmLabel="Poista"
        destructive
        onConfirm={() => {
          setConfirming(false);
          onDelete();
        }}
      />
    </>
  );
}

// ─── Locating a row ───────────────────────────────────────────────────────────

interface LocatedRow {
  packageId: string;
  col2: Col2Selection;
  col3?: Col3Selection;
  subtask?: PreviewSubtask;
  documentSubtask?: PreviewSubtask;
}

/**
 * Find which columns have to be selected for a given task id to be visible.
 *
 * Used to reveal a row the moment it is added, so its detail panel opens
 * without the user hunting for it in the list.
 */
function locate(packages: PreviewPackage[], id: string): LocatedRow | null {
  for (const pkg of packages) {
    for (const task of pkg.tasks) {
      if (task.id === id) {
        return { packageId: pkg.id, col2: { kind: "procurement-task", task } };
      }
      const sub = task.subtasks.find((s) => s.id === id);
      if (sub) {
        return {
          packageId: pkg.id,
          col2: { kind: "procurement-task", task },
          subtask: sub,
        };
      }
    }

    for (const ws of pkg.workSections) {
      for (const task of ws.inspectionTasks) {
        if (task.id === id) {
          return {
            packageId: pkg.id,
            col2: { kind: "ws", ws },
            col3: { kind: "inspection-task", task },
          };
        }
      }
      for (const task of ws.documentTasks) {
        if (task.id === id) {
          return {
            packageId: pkg.id,
            col2: { kind: "ws", ws },
            col3: { kind: "document-task", task },
          };
        }
        const sub = task.subtasks.find((s) => s.id === id);
        if (sub) {
          return {
            packageId: pkg.id,
            col2: { kind: "ws", ws },
            col3: { kind: "document-task", task },
            documentSubtask: sub,
          };
        }
      }
    }
  }
  return null;
}

// ─── Selection types ──────────────────────────────────────────────────────────

type Col2Selection =
  | { kind: "procurement-task"; task: PreviewTask }
  | { kind: "ws"; ws: PreviewWorkSection };

type Col3Selection =
  | { kind: "inspection-task"; task: PreviewInspectionTask }
  | { kind: "document-task"; task: PreviewDocumentTask };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pkgErrorCount(pkg: PreviewPackage): number {
  return (
    (pkg.errors?.length ?? 0) +
    pkg.tasks.reduce((s, t) => s + (t.errors?.length ?? 0), 0) +
    pkg.workSections.reduce(
      (s, ws) =>
        s +
        (ws.errors?.length ?? 0) +
        ws.inspectionTasks.reduce((ts, t) => ts + (t.errors?.length ?? 0), 0) +
        ws.documentTasks.reduce((ts, t) => ts + (t.errors?.length ?? 0), 0),
      0
    )
  );
}

function ErrorList({ errors }: { errors: string[] }) {
  return (
    <div className="rounded-md bg-destructive/5 border border-destructive/20 px-2.5 py-2 space-y-3">
      {errors.map((e, i) => (
        <p key={i} className="text-xs text-destructive leading-tight">
          {e}
        </p>
      ))}
    </div>
  );
}

// ─── Column 1: Package list ───────────────────────────────────────────────────

function PackageRows({
  packages,
  selectedId,
  onSelect,
  touchedPackageIds,
}: {
  packages: PreviewPackage[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  touchedPackageIds?: Set<string>;
}) {
  return (
    <>
      <SectionHeader
        icon={<ProcurementPackageIcon className="h-3 w-3" />}
        label={`Hankintapaketit (${packages.length})`}
      />
      {packages.length === 0 && <EmptyCol label="Ei hankintapaketteja" />}
      {packages.map((pkg) => {
        const errCount = pkgErrorCount(pkg);
        const hasContent =
          pkg.tasks.length > 0 ||
          pkg.workSections.some(
            (ws) => ws.inspectionTasks.length > 0 || ws.documentTasks.length > 0
          );
        const isSel = pkg.id === selectedId;
        return (
          <Row
            key={pkg.id}
            isSelected={isSel}
            hasError={errCount > 0 && !isSel}
            hasChildren={hasContent}
            isTouched={touchedPackageIds?.has(pkg.id)}
            onClick={() => onSelect(pkg.id)}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <PackageCode code={pkg.code} isSelected={isSel} />
                {errCount > 0 && (
                  <RowBadge count={errCount} isSelected={isSel} hasError />
                )}
              </div>
              <RowLabel value={pkg.name || "—"} isSelected={isSel} />
            </div>
          </Row>
        );
      })}
    </>
  );
}

function PackageCode({ code, isSelected }: { code: string; isSelected: boolean }) {
  const { isActive } = useColumnState();
  const selected = isSelected && isActive;
  return (
    <span
      className={`font-mono text-[11px] shrink-0 ${
        selected ? "text-primary-foreground/70" : "text-muted-foreground"
      }`}
    >
      {code}
    </span>
  );
}

function RowLabel({ value, isSelected }: { value: string; isSelected: boolean }) {
  const { isActive } = useColumnState();
  const selected = isSelected && isActive;
  return (
    <div
      className={`text-xs truncate ${
        selected ? "text-primary-foreground" : "text-foreground"
      }`}
    >
      {value}
    </div>
  );
}

function RowBadge({
  count,
  isSelected,
  hasError,
}: {
  count: number;
  isSelected: boolean;
  hasError?: boolean;
}) {
  const { isActive } = useColumnState();
  const selected = isSelected && isActive;
  const parentSelected = isSelected && !isActive;
  const className = selected
    ? "border-primary-foreground/50 text-primary-foreground/70 bg-transparent"
    : parentSelected
    ? "border-muted-foreground/40 text-muted-foreground bg-transparent"
    : hasError
    ? "border-destructive/40 text-destructive bg-destructive/5"
    : "border-muted-foreground/40 text-muted-foreground bg-transparent";
  return (
    <Badge
      variant="outline"
      className={`text-[10px] px-1 py-0 leading-tight shrink-0 transition-none ${className}`}
    >
      {count}
    </Badge>
  );
}

function RowInlineLabel({
  text,
  isSelected,
}: {
  text: string;
  isSelected: boolean;
}) {
  const { isActive } = useColumnState();
  const selected = isSelected && isActive;
  return (
    <span className={`text-xs truncate ${selected ? "text-primary-foreground" : ""}`}>
      {text}
    </span>
  );
}

// ─── Column 2: Package content ────────────────────────────────────────────────

function PackageContent({
  pkg,
  selected,
  onSelect,
  editing,
  touchedWorkSectionIds,
  touchedTaskIds,
}: {
  pkg: PreviewPackage;
  selected: Col2Selection | null;
  onSelect: (sel: Col2Selection) => void;
  editing?: TemplateEditingApi;
  touchedWorkSectionIds?: Set<string>;
  touchedTaskIds?: Set<string>;
}) {
  const wsWithTasks = pkg.workSections.filter(
    (ws) =>
      !!editing ||
      ws.inspectionTasks.length > 0 ||
      ws.documentTasks.length > 0 ||
      !!ws.errors?.length
  );

  const isTaskSel = (t: PreviewTask) =>
    selected?.kind === "procurement-task" && selected.task.id === t.id;
  const isWsSel = (ws: PreviewWorkSection) =>
    selected?.kind === "ws" && selected.ws.id === ws.id;

  return (
    <>
      <SectionHeader icon={<ProcurementPackageIcon className="h-3 w-3" />} label="Hankintapaketti" />
      <DetailPanel>
        {!!pkg.errors?.length && <ErrorList errors={pkg.errors} />}
        <DetailField
          label="Koodi"
          value={<span className="font-mono text-[11px]">{pkg.code}</span>}
        />
        <DetailField label="Nimi" value={pkg.name || "—"} />
      </DetailPanel>

      {(pkg.tasks.length > 0 || !!editing) && (
        <>
          <SectionHeader
            icon={<TaskListIcon className="h-3 w-3" />}
            label={`Hankintapakettitehtävät (${pkg.tasks.length})`}
          />
          {pkg.tasks.map((task) => {
            const hasErr = !!task.errors?.length;
            const isSel = isTaskSel(task);
            const hasDetail =
              !!task.description?.trim() || task.subtasks.length > 0 || hasErr;
            return (
              <Row
                key={task.id}
                isSelected={isSel}
                hasError={hasErr && !isSel}
                hasChildren={hasDetail}
                isTouched={touchedTaskIds?.has(task.id)}
                isStruck={task.isDeleted}
                onClick={() => onSelect({ kind: "procurement-task", task })}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  {hasErr && (
                    <RowBadge
                      count={task.errors!.length}
                      isSelected={isSel}
                      hasError
                    />
                  )}
                  <RowInlineLabel text={task.name || "—"} isSelected={isSel} />
                </div>
              </Row>
            );
          })}
          {editing && (
            <AddTaskRow
              kind="PROCUREMENT"
              present={pkg.tasks.filter((t) => !t.isDeleted).map((t) => t.name)}
              label="Lisää hankintapakettitehtävä"
              onAdd={(name) =>
                editing.onAddTask({
                  kind: "PROCUREMENT",
                  name,
                  packageId: pkg.id,
                  workSectionId: null,
                })
              }
            />
          )}
        </>
      )}

      {wsWithTasks.length > 0 && (
        <>
          <SectionHeader
            icon={<WorkSectionIcon className="h-3 w-3" />}
            label={`Työvaiheet (${wsWithTasks.length})`}
          />
          {wsWithTasks.map((ws) => {
            const wsErrCount =
              (ws.errors?.length ?? 0) +
              ws.inspectionTasks.reduce(
                (s, t) => s + (t.errors?.length ?? 0),
                0
              ) +
              ws.documentTasks.reduce(
                (s, t) => s + (t.errors?.length ?? 0),
                0
              );
            const hasErr = wsErrCount > 0;
            const isSel = isWsSel(ws);
            return (
              <Row
                key={ws.id}
                isSelected={isSel}
                hasError={hasErr && !isSel}
                hasChildren
                isTouched={touchedWorkSectionIds?.has(ws.id)}
                onClick={() => onSelect({ kind: "ws", ws })}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <PackageCode
                      code={ws.code === "unassigned" ? "—" : ws.code}
                      isSelected={isSel}
                    />
                    {wsErrCount > 0 && (
                      <RowBadge
                        count={wsErrCount}
                        isSelected={isSel}
                        hasError
                      />
                    )}
                  </div>
                  <RowLabel value={ws.name || "—"} isSelected={isSel} />
                </div>
              </Row>
            );
          })}
        </>
      )}
    </>
  );
}

// ─── Column 3 variants ────────────────────────────────────────────────────────

function ProcurementTaskContent({
  task,
  selectedSubtaskId,
  onSelect,
  editing,
  touchedTaskIds,
}: {
  task: PreviewTask;
  selectedSubtaskId: string | null;
  onSelect: (sub: PreviewSubtask) => void;
  editing?: TemplateEditingApi;
  touchedTaskIds?: Set<string>;
}) {
  const struck = !!task.isDeleted;
  const edit = struck ? undefined : editing;
  return (
    <>
      <SectionHeader icon={<TaskListIcon className="h-3 w-3" />} label="Tehtävä" />
      <DetailPanel>
        {editing && struck && (
          <DeletedNotice onUndo={() => editing.onUndeleteTask(task.id)} />
        )}
        {!!task.errors?.length && <ErrorList errors={task.errors} />}
        <DetailField label="Nimike" value={task.name || "—"} />
        {edit ? (
          <DetailField
            label="Määräaika"
            value={
              <DeadlineOffsetEditor
                value={offsetFromColumns(
                  task.startTimingOffsetDays,
                  task.endTimingOffsetDays
                )}
                onChange={(offset) =>
                  edit.onPatchTask(task.id, { offset }, !!task.isNew)
                }
              />
            }
          />
        ) : (
          <DetailField label="Määräaika" value={task.deadlineText || "—"} />
        )}
        {!edit && task.description?.trim() && (
          <DetailField label="Kuvaus" value={task.description} />
        )}
        {edit && (
          <DeleteTaskButton
            hasChildren={task.subtasks.length > 0}
            onDelete={() =>
              edit.onDeleteTask({ id: task.id, isNew: !!task.isNew })
            }
          />
        )}
      </DetailPanel>
      {task.subtasks.length === 0 && !editing ? (
        <EmptyCol label="Ei alitehtäviä" />
      ) : (
        <>
          <SectionHeader
            icon={<SuccessIcon className="h-3 w-3" />}
            label={`Alitehtävät (${task.subtasks.length})`}
          />
          {task.subtasks.map((sub) => {
            const isSel = sub.id === selectedSubtaskId;
            return (
              <Row
                key={sub.id}
                isSelected={isSel}
                hasChildren={!!sub.description || !!editing}
                isTouched={touchedTaskIds?.has(sub.id)}
                isStruck={sub.isDeleted}
                onClick={() => onSelect(sub)}
              >
                <RowInlineLabel text={sub.name || "—"} isSelected={isSel} />
              </Row>
            );
          })}
          {editing && !task.isDeleted && (
            <div className="px-3 py-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-full text-xs"
                onClick={() =>
                  editing.onAddSubtask({
                    parentId: task.id,
                    isNewParent: !!task.isNew,
                    packageId: task.packageId ?? null,
                    workSectionId: task.workSectionId ?? null,
                  })
                }
              >
                <AddIcon className="mr-1.5 h-3 w-3" />
                Lisää alitehtävä
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}

function WorkSectionContent({
  ws,
  selected,
  onSelect,
  editing,
  packageId,
  touchedTaskIds,
}: {
  ws: PreviewWorkSection;
  selected: Col3Selection | null;
  onSelect: (sel: Col3Selection) => void;
  editing?: TemplateEditingApi;
  packageId?: string | null;
  touchedTaskIds?: Set<string>;
}) {
  return (
    <>
      <SectionHeader icon={<WorkSectionIcon className="h-3 w-3" />} label="Työvaihe" />
      <DetailPanel>
        {!!ws.errors?.length && <ErrorList errors={ws.errors} />}
        <DetailField
          label="Koodi"
          value={
            <span className="font-mono text-[11px]">
              {ws.code === "unassigned" ? "—" : ws.code}
            </span>
          }
        />
        <DetailField label="Nimi" value={ws.name || "—"} />
      </DetailPanel>

      {(ws.inspectionTasks.length > 0 || !!editing) && (
        <>
          <SectionHeader
            icon={<InspectionTaskIcon className="h-3 w-3" />}
            label={`Tarkastustehtävät (${ws.inspectionTasks.length})`}
          />
          {ws.inspectionTasks.map((task) => {
            const hasErr = !!task.errors?.length;
            const isSel =
              selected?.kind === "inspection-task" &&
              selected.task.id === task.id;
            return (
              <Row
                key={task.id}
                isSelected={isSel}
                hasError={hasErr && !isSel}
                hasChildren
                isTouched={touchedTaskIds?.has(task.id)}
                isStruck={task.isDeleted}
                onClick={() => onSelect({ kind: "inspection-task", task })}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  {hasErr && (
                    <RowBadge
                      count={task.errors!.length}
                      isSelected={isSel}
                      hasError
                    />
                  )}
                  <RowInlineLabel text={task.name} isSelected={isSel} />
                </div>
              </Row>
            );
          })}
          {editing && (
            <AddTaskRow
              kind="INSPECTION"
              present={ws.inspectionTasks
                .filter((t) => !t.isDeleted)
                .map((t) => t.name)}
              label="Lisää tarkastustehtävä"
              onAdd={(name) =>
                editing.onAddTask({
                  kind: "INSPECTION",
                  name,
                  packageId: packageId ?? null,
                  workSectionId: ws.id,
                })
              }
            />
          )}
        </>
      )}

      {(ws.documentTasks.length > 0 || !!editing) && (
        <>
          <SectionHeader
            icon={<DocumentIcon className="h-3 w-3" />}
            label={`Dokumenttitehtävät (${ws.documentTasks.length})`}
          />
          {ws.documentTasks.map((task) => {
            const hasErr = !!task.errors?.length;
            const isSel =
              selected?.kind === "document-task" &&
              selected.task.id === task.id;
            return (
              <Row
                key={task.id}
                isSelected={isSel}
                hasError={hasErr && !isSel}
                hasChildren
                isTouched={touchedTaskIds?.has(task.id)}
                isStruck={task.isDeleted}
                onClick={() => onSelect({ kind: "document-task", task })}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  {hasErr && (
                    <RowBadge
                      count={task.errors!.length}
                      isSelected={isSel}
                      hasError
                    />
                  )}
                  <RowInlineLabel text={task.name} isSelected={isSel} />
                </div>
              </Row>
            );
          })}
          {editing && (
            <AddTaskRow
              kind="DOCUMENT"
              present={ws.documentTasks
                .filter((t) => !t.isDeleted)
                .map((t) => t.name)}
              label="Lisää dokumenttitehtävä"
              onAdd={(name) =>
                editing.onAddTask({
                  kind: "DOCUMENT",
                  name,
                  packageId: packageId ?? null,
                  workSectionId: ws.id,
                })
              }
            />
          )}
        </>
      )}

      {!editing &&
        ws.inspectionTasks.length === 0 &&
        ws.documentTasks.length === 0 && <EmptyCol label="Ei tehtäviä" />}
    </>
  );
}

function InspectionTaskDetail({
  task,
  editing,
}: {
  task: PreviewInspectionTask;
  editing?: TemplateEditingApi;
}) {
  const struck = !!task.isDeleted;
  const edit = struck ? undefined : editing;
  const congridMissing = !task.congridId.trim();
  return (
    <>
      <SectionHeader
        icon={<InspectionTaskIcon className="h-3 w-3" />}
        label="Tarkastustehtävä"
      />
      <DetailPanel>
        {editing && struck && (
          <DeletedNotice onUndo={() => editing.onUndeleteTask(task.id)} />
        )}
        {!!task.errors?.length && !struck && <ErrorList errors={task.errors} />}
        <DetailField label="Nimike" value={task.name || "—"} />
        {edit ? (
          <DetailField
            label="Määräaika"
            value={
              <DeadlineOffsetEditor
                value={offsetFromColumns(
                  task.startTimingOffsetDays,
                  task.endTimingOffsetDays
                )}
                onChange={(offset) =>
                  edit.onPatchTask(task.id, { offset }, !!task.isNew)
                }
              />
            }
          />
        ) : (
          <DetailField label="Määräaika" value={task.deadlineText || "—"} />
        )}
        {!edit && task.description && (
          <DetailField label="Kuvaus" value={task.description} />
        )}
        {edit ? (
          <DetailField
            label="Congrid ID"
            value={
              <div className="space-y-1">
                <Input
                  value={task.congridId}
                  className={`h-7 font-mono text-[11px] ${
                    congridMissing ? "border-destructive" : ""
                  }`}
                  placeholder="Congrid-tarkastuspohjan ID"
                  onChange={(e) =>
                    edit.onPatchTask(
                      task.id,
                      { congridInspectionTemplateId: e.target.value },
                      !!task.isNew
                    )
                  }
                />
                {congridMissing && (
                  <div className="text-[11px] text-destructive">
                    Congrid ID vaaditaan
                  </div>
                )}
              </div>
            }
          />
        ) : (
          <DetailField
            label="Congrid ID"
            value={
              <span className="font-mono text-[11px] bg-muted px-1.5 py-0.5 rounded">
                {task.congridId}
              </span>
            }
          />
        )}
        {edit && (
          <DeleteTaskButton
            onDelete={() =>
              edit.onDeleteTask({ id: task.id, isNew: !!task.isNew })
            }
          />
        )}
      </DetailPanel>
    </>
  );
}

function DocumentTaskContent({
  task,
  selectedSubtaskId,
  onSelect,
  editing,
  touchedTaskIds,
}: {
  task: PreviewDocumentTask;
  selectedSubtaskId: string | null;
  onSelect: (sub: PreviewSubtask) => void;
  editing?: TemplateEditingApi;
  touchedTaskIds?: Set<string>;
}) {
  const struck = !!task.isDeleted;
  const edit = struck ? undefined : editing;
  return (
    <>
      <SectionHeader
        icon={<DocumentIcon className="h-3 w-3" />}
        label="Dokumenttitehtävä"
      />
      <DetailPanel>
        {editing && struck && (
          <DeletedNotice onUndo={() => editing.onUndeleteTask(task.id)} />
        )}
        {!!task.errors?.length && <ErrorList errors={task.errors} />}
        <DetailField label="Nimike" value={task.name || "—"} />
        {edit ? (
          <DetailField
            label="Määräaika"
            value={
              <DeadlineOffsetEditor
                value={offsetFromColumns(
                  task.startTimingOffsetDays,
                  task.endTimingOffsetDays
                )}
                onChange={(offset) =>
                  edit.onPatchTask(task.id, { offset }, !!task.isNew)
                }
              />
            }
          />
        ) : (
          <DetailField label="Määräaika" value={task.deadlineText || "—"} />
        )}
        {edit && (
          <DeleteTaskButton
            hasChildren={task.subtasks.length > 0}
            onDelete={() =>
              edit.onDeleteTask({ id: task.id, isNew: !!task.isNew })
            }
          />
        )}
      </DetailPanel>
      {task.subtasks.length === 0 && !editing ? (
        <EmptyCol label="Ei alitehtäviä" />
      ) : (
        <>
          <SectionHeader
            icon={<SuccessIcon className="h-3 w-3" />}
            label={`Alitehtävät (${task.subtasks.length})`}
          />
          {task.subtasks.map((sub) => {
            const isSel = sub.id === selectedSubtaskId;
            return (
              <Row
                key={sub.id}
                isSelected={isSel}
                hasChildren={!!sub.description || !!editing}
                isTouched={touchedTaskIds?.has(sub.id)}
                isStruck={sub.isDeleted}
                onClick={() => onSelect(sub)}
              >
                <RowInlineLabel text={sub.name || "—"} isSelected={isSel} />
              </Row>
            );
          })}
          {editing && !task.isDeleted && (
            <div className="px-3 py-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-full text-xs"
                onClick={() =>
                  editing.onAddSubtask({
                    parentId: task.id,
                    isNewParent: !!task.isNew,
                    packageId: task.packageId ?? null,
                    workSectionId: task.workSectionId ?? null,
                  })
                }
              >
                <AddIcon className="mr-1.5 h-3 w-3" />
                Lisää alitehtävä
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}

function SubtaskDetail({
  subtask,
  editing,
}: {
  subtask: PreviewSubtask;
  editing?: TemplateEditingApi;
}) {
  const struck = !!subtask.isDeleted;
  const edit = struck ? undefined : editing;
  return (
    <>
      <SectionHeader
        icon={<SuccessIcon className="h-3 w-3" />}
        label="Alitehtävä"
      />
      <DetailPanel>
        {editing && struck && (
          <DeletedNotice onUndo={() => editing.onUndeleteTask(subtask.id)} />
        )}
        {edit ? (
          <>
            <DetailField
              label="Nimike"
              value={
                <Input
                  value={subtask.name}
                  className="h-7 text-xs"
                  placeholder="Alitehtävän nimike"
                  onChange={(e) =>
                    edit.onPatchTask(
                      subtask.id,
                      { name: e.target.value },
                      !!subtask.isNew
                    )
                  }
                />
              }
            />
            <DetailField
              label="Kuvaus"
              value={
                <Textarea
                  value={subtask.description ?? ""}
                  className="text-xs"
                  rows={3}
                  placeholder="Alitehtävän kuvaus"
                  onChange={(e) =>
                    edit.onPatchTask(
                      subtask.id,
                      { description: e.target.value || null },
                      !!subtask.isNew
                    )
                  }
                />
              }
            />
            <DeleteTaskButton
              onDelete={() =>
                edit.onDeleteTask({
                  id: subtask.id,
                  isNew: !!subtask.isNew,
                })
              }
            />
          </>
        ) : (
          <>
            <DetailField label="Nimike" value={subtask.name || "—"} />
            {subtask.description && (
              <DetailField label="Kuvaus" value={subtask.description} />
            )}
          </>
        )}
      </DetailPanel>
    </>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

interface Props {
  packages: PreviewPackage[];
  maxHeight?: string;
  editing?: TemplateEditingApi;
  /** Reveal this task id once it appears in `packages`, then report back. */
  revealTaskId?: string | null;
  onRevealed?: () => void;
  /** Containers changed during this editing session, tinted to track work. */
  touchedPackageIds?: Set<string>;
  touchedWorkSectionIds?: Set<string>;
  /** Individual tehtävät added or modified this session. */
  touchedTaskIds?: Set<string>;
}

export function TaskTemplateColumnView({
  packages,
  maxHeight = "60vh",
  editing,
  revealTaskId,
  onRevealed,
  touchedPackageIds,
  touchedWorkSectionIds,
  touchedTaskIds,
}: Props) {
  const [selectedPkgId, setSelectedPkgId] = useState<string | null>(null);
  const [col2Ref, setSelectedCol2] = useState<Col2Selection | null>(null);
  const [col3Ref, setSelectedCol3] = useState<Col3Selection | null>(null);
  const [subtaskRef, setSelectedSubtask] = useState<PreviewSubtask | null>(null);
  const [documentSubtaskRef, setSelectedDocumentSubtask] =
    useState<PreviewSubtask | null>(null);

  const selectedPkg = packages.find((p) => p.id === selectedPkgId) ?? null;

  // A freshly added row selects itself, so its detail panel opens without the
  // user having to find and click it. Runs once `packages` actually contains
  // the row, since the draft rebuild lands a render later than the click.
  useEffect(() => {
    if (!revealTaskId) return;
    const found = locate(packages, revealTaskId);
    if (!found) return;
    setSelectedPkgId(found.packageId);
    setSelectedCol2(found.col2);
    setSelectedCol3(found.col3 ?? null);
    setSelectedSubtask(found.subtask ?? null);
    setSelectedDocumentSubtask(found.documentSubtask ?? null);
    onRevealed?.();
  }, [revealTaskId, packages, onRevealed]);

  // Selection state stores what was clicked; `packages` is rebuilt on every
  // draft edit with fresh object identities, so each selection is re-resolved
  // by id here. Rendering the stored object instead would freeze a detail
  // panel at its click-time snapshot until the user reselected it. A selection
  // whose row has since been deleted resolves to null and clears the panel.
  const selectedCol2: Col2Selection | null = (() => {
    if (!col2Ref || !selectedPkg) return null;
    if (col2Ref.kind === "procurement-task") {
      const task = selectedPkg.tasks.find((t) => t.id === col2Ref.task.id);
      return task ? { kind: "procurement-task", task } : null;
    }
    const ws = selectedPkg.workSections.find((w) => w.id === col2Ref.ws.id);
    return ws ? { kind: "ws", ws } : null;
  })();

  const selectedCol3: Col3Selection | null = (() => {
    if (!col3Ref || selectedCol2?.kind !== "ws") return null;
    if (col3Ref.kind === "inspection-task") {
      const task = selectedCol2.ws.inspectionTasks.find(
        (t) => t.id === col3Ref.task.id
      );
      return task ? { kind: "inspection-task", task } : null;
    }
    const task = selectedCol2.ws.documentTasks.find(
      (t) => t.id === col3Ref.task.id
    );
    return task ? { kind: "document-task", task } : null;
  })();

  const selectedSubtask: PreviewSubtask | null =
    subtaskRef && selectedCol2?.kind === "procurement-task"
      ? selectedCol2.task.subtasks.find((sub) => sub.id === subtaskRef.id) ?? null
      : null;

  const selectedDocumentSubtask: PreviewSubtask | null =
    documentSubtaskRef && selectedCol3?.kind === "document-task"
      ? selectedCol3.task.subtasks.find(
          (sub) => sub.id === documentSubtaskRef.id
        ) ?? null
      : null;

  // Items navigable in column 1 (procurement tasks + work sections). While
  // editing, a työvaihe with no tehtäviä stays listed so its first one can be
  // added.
  const col1Items: Col2Selection[] = selectedPkg
    ? [
        ...selectedPkg.tasks.map((t): Col2Selection => ({
          kind: "procurement-task",
          task: t,
        })),
        ...selectedPkg.workSections
          .filter(
            (ws) =>
              !!editing ||
              ws.inspectionTasks.length > 0 ||
              ws.documentTasks.length > 0 ||
              !!ws.errors?.length
          )
          .map((ws): Col2Selection => ({ kind: "ws", ws })),
      ]
    : [];

  // Items navigable in column 2 (depends on what's selected in column 1).
  const col2WsTaskItems: Col3Selection[] | null =
    selectedCol2?.kind === "ws"
      ? [
          ...selectedCol2.ws.inspectionTasks.map((t): Col3Selection => ({
            kind: "inspection-task",
            task: t,
          })),
          ...selectedCol2.ws.documentTasks.map((t): Col3Selection => ({
            kind: "document-task",
            task: t,
          })),
        ]
      : null;

  // Items navigable in column 3 (document task subtasks only).
  const col3DocSubtasks: PreviewSubtask[] | null =
    selectedCol3?.kind === "document-task" ? selectedCol3.task.subtasks : null;

  function selectPackage(id: string) {
    setSelectedPkgId(id);
    setSelectedCol2(null);
    setSelectedCol3(null);
    setSelectedSubtask(null);
    setSelectedDocumentSubtask(null);
  }

  function selectCol2(sel: Col2Selection) {
    setSelectedCol2(sel);
    setSelectedCol3(null);
    setSelectedSubtask(null);
    setSelectedDocumentSubtask(null);
  }

  function selectCol3(sel: Col3Selection) {
    setSelectedCol3(sel);
    setSelectedDocumentSubtask(null);
  }

  // ─── Build column descriptors ────────────────────────────────────────────────

  const col0Nav: ColumnNavigation = {
    length: packages.length,
    selectedIndex: selectedPkgId
      ? packages.findIndex((p) => p.id === selectedPkgId)
      : null,
    onSelectIndex: (i) => selectPackage(packages[i].id),
  };

  const col1Nav: ColumnNavigation | undefined = selectedPkg
    ? {
        length: col1Items.length,
        selectedIndex: (() => {
          if (!selectedCol2) return null;
          const idx = col1Items.findIndex((item) => {
            if (item.kind !== selectedCol2.kind) return false;
            if (
              item.kind === "procurement-task" &&
              selectedCol2.kind === "procurement-task"
            )
              return item.task.id === selectedCol2.task.id;
            if (item.kind === "ws" && selectedCol2.kind === "ws")
              return item.ws.id === selectedCol2.ws.id;
            return false;
          });
          return idx >= 0 ? idx : null;
        })(),
        onSelectIndex: (i) => selectCol2(col1Items[i]),
        onClear: () => {
          setSelectedCol2(null);
          setSelectedCol3(null);
          setSelectedSubtask(null);
          setSelectedDocumentSubtask(null);
        },
      }
    : undefined;

  let col2Nav: ColumnNavigation | undefined;
  if (selectedCol2?.kind === "procurement-task") {
    const subs = selectedCol2.task.subtasks;
    col2Nav = {
      length: subs.length,
      selectedIndex: selectedSubtask
        ? (() => {
            const i = subs.findIndex((s) => s.id === selectedSubtask.id);
            return i >= 0 ? i : null;
          })()
        : null,
      onSelectIndex: (i) => setSelectedSubtask(subs[i]),
      onClear: () => setSelectedSubtask(null),
    };
  } else if (selectedCol2?.kind === "ws" && col2WsTaskItems) {
    const items = col2WsTaskItems;
    col2Nav = {
      length: items.length,
      selectedIndex: selectedCol3
        ? (() => {
            const i = items.findIndex((item) => {
              if (
                item.kind === "inspection-task" &&
                selectedCol3.kind === "inspection-task"
              )
                return item.task.id === selectedCol3.task.id;
              if (
                item.kind === "document-task" &&
                selectedCol3.kind === "document-task"
              )
                return item.task.id === selectedCol3.task.id;
              return false;
            });
            return i >= 0 ? i : null;
          })()
        : null,
      onSelectIndex: (i) => selectCol3(items[i]),
      onClear: () => {
        setSelectedCol3(null);
        setSelectedDocumentSubtask(null);
      },
    };
  }

  const col3Nav: ColumnNavigation | undefined = col3DocSubtasks
    ? {
        length: col3DocSubtasks.length,
        selectedIndex: selectedDocumentSubtask
          ? (() => {
              const i = col3DocSubtasks.findIndex(
                (s) => s.id === selectedDocumentSubtask.id
              );
              return i >= 0 ? i : null;
            })()
          : null,
        onSelectIndex: (i) => setSelectedDocumentSubtask(col3DocSubtasks[i]),
        onClear: () => setSelectedDocumentSubtask(null),
      }
    : undefined;

  // ─── Column contents ─────────────────────────────────────────────────────────

  const col2Content = (() => {
    if (!selectedCol2) return <EmptyCol />;
    if (selectedCol2.kind === "procurement-task") {
      return (
        <ProcurementTaskContent
          task={selectedCol2.task}
          selectedSubtaskId={selectedSubtask?.id ?? null}
          onSelect={setSelectedSubtask}
          editing={editing}
          touchedTaskIds={touchedTaskIds}
        />
      );
    }
    return (
      <WorkSectionContent
        ws={selectedCol2.ws}
        selected={selectedCol3}
        onSelect={selectCol3}
        editing={editing}
        packageId={selectedPkg?.id ?? null}
        touchedTaskIds={touchedTaskIds}
      />
    );
  })();

  const col3Content = (() => {
    if (selectedCol2?.kind === "procurement-task" && selectedSubtask) {
      return (
        <SubtaskDetail subtask={selectedSubtask} editing={editing} />
      );
    }
    if (selectedCol3?.kind === "document-task") {
      return (
        <DocumentTaskContent
          task={selectedCol3.task}
          selectedSubtaskId={selectedDocumentSubtask?.id ?? null}
          onSelect={setSelectedDocumentSubtask}
          editing={editing}
          touchedTaskIds={touchedTaskIds}
        />
      );
    }
    if (selectedCol3?.kind === "inspection-task") {
      return (
        <InspectionTaskDetail task={selectedCol3.task} editing={editing} />
      );
    }
    return <EmptyCol />;
  })();

  const col4Content = selectedDocumentSubtask ? (
    <SubtaskDetail subtask={selectedDocumentSubtask} editing={editing} />
  ) : (
    <EmptyCol />
  );

  const columns: ColumnDescriptor[] = [
    {
      key: "packages",
      navigation: col0Nav,
      content: (
        <PackageRows
          packages={packages}
          selectedId={selectedPkgId}
          onSelect={selectPackage}
          touchedPackageIds={touchedPackageIds}
        />
      ),
    },
    {
      key: "package-content",
      navigation: col1Nav,
      content: selectedPkg ? (
        col1Items.length === 0 && !editing ? (
          <EmptyCol label="Ei tehtäviä" />
        ) : (
          <PackageContent
            pkg={selectedPkg}
            selected={selectedCol2}
            onSelect={selectCol2}
            editing={editing}
            touchedWorkSectionIds={touchedWorkSectionIds}
            touchedTaskIds={touchedTaskIds}
          />
        )
      ) : (
        <EmptyCol />
      ),
    },
    {
      key: "col2",
      navigation: col2Nav,
      content: col2Content,
    },
    {
      key: "col3",
      navigation: col3Nav,
      content: col3Content,
    },
    {
      key: "col4",
      content: col4Content,
    },
  ];

  return <ColumnLayout columns={columns} height={maxHeight} />;
}
